import type { Proposal } from "../api/types";

/**
 * One concrete next-action the user can take after the agent's last
 * turn. `prefill` is what we put in the input box on tap — never
 * auto-sent, the operator still presses ↩ or Send. The pill is
 * intentionally a *prefilled draft*, not a one-tap action, so a user
 * with the wrong intent can edit it instead of triggering it.
 */
export interface Suggestion {
  /** Short label for the chip. */
  label: string;
  /** Text inserted into the agent input on tap. */
  prefill: string;
  /** Source semantics so the shared UI can distinguish clarifications later. */
  kind?: "clarification" | "missing_detail" | "follow_up";
}

interface Inputs {
  voix: string;
  details: string;
  proposals: Proposal[];
  structuredActions?: Suggestion[];
}

/**
 * Compute up to 5 follow-up suggestions from the last agent turn.
 *
 * Rules — kept narrow on purpose. A weak generic suggestion ("Voulez-
 * vous autre chose ?") trains operators to ignore the strip; better to
 * show nothing than to show filler. Every rule below requires a real
 * signal in the just-landed proposals or in the agent's text.
 *
 * Currently:
 *   1. Agent asked a question → return [] (don't compete with the
 *      question — the user's next action IS to answer it).
 *   2. ≥1 commande_line proposal with a known client → "Idem pour la
 *      prochaine livraison de <client>". Replicates a recurring order
 *      without retyping it.
 *   3. ≥1 commande_line proposal with NO delivery_date → "Date de
 *      livraison : ". Lets the operator add the date the agent didn't
 *      infer.
 *   4. ≥1 inventory_movement proposal → "Ajoute aussi : ". Cheap
 *      starter for the next item in the same stock-take session.
 */
export function computeSuggestions(input: Inputs): Suggestion[] {
  const structured = dedupeSuggestions(input.structuredActions ?? []);
  const clarifications = structured.filter((item) => item.kind === "clarification");
  if (clarifications.length > 0) {
    return clarifications.slice(0, 5);
  }

  const out: Suggestion[] = [...structured];

  // Rule 1: never compete with a clarifying question.
  if (endsWithQuestion(input.voix)) return structured.slice(0, 5);

  const commandeLines = input.proposals.filter(
    (p) => p.type === "commande_line",
  );
  const movements = input.proposals.filter(
    (p) => p.type === "inventory_movement",
  );

  // Rule 2: replicate a recurring order. Use the most-common client in
  // this batch — almost always all the same one in real ops.
  const clients = new Set(
    commandeLines
      .map((p) => String(p.payload.client ?? "").trim())
      .filter(Boolean),
  );
  if (clients.size === 1) {
    const [client] = clients;
    out.push({
      label: `Idem prochaine livraison ${client}`,
      prefill: `Refais la même commande pour ${client}, prochaine livraison.`,
      kind: "follow_up",
    });
  }

  // Rule 3: prompt for the missing date.
  const missingDate = commandeLines.some((p) => !p.payload.delivery_date);
  if (missingDate) {
    out.push({
      label: "Ajouter la date de livraison",
      prefill: "Date de livraison : ",
      kind: "missing_detail",
    });
  }

  // Rule 4: starter for the next mouvement in a stock-take.
  if (movements.length > 0) {
    out.push({
      label: "Ajouter un autre mouvement",
      prefill: "Ajoute aussi : ",
      kind: "follow_up",
    });
  }

  return dedupeSuggestions(out).slice(0, 5);
}

function endsWithQuestion(text: string): boolean {
  return /[?？]\s*$/.test(text.trim());
}

function dedupeSuggestions(items: Suggestion[]): Suggestion[] {
  const seen = new Set<string>();
  const out: Suggestion[] = [];
  for (const item of items) {
    const key = `${item.label}\u0000${item.prefill}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item);
  }
  return out;
}
